import React, { useEffect, useMemo, useCallback } from "react";
import { Container, Row, Col, Card, Badge, Button, Nav } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import web5 from "../img/teasale.jpg";

const TeaSale = () => {
  const navigate = useNavigate();

  // ========= Theme (Corporate) =========
  const theme = useMemo(
    () => ({
      bg: "#f6f8fb",
      surface: "#ffffff",
      ink: "#0f172a",
      slate: "#334155",
      muted: "#64748b",
      border: "#e5e7eb",
      navy: "#0b1220",
      accent: "#1d4ed8",
      accent2: "#0ea5e9",
      success: "#16a34a",
      shadow: "0 24px 48px rgba(2, 6, 23, 0.08)",
      shadowSoft: "0 12px 24px rgba(2, 6, 23, 0.06)",
    }),
    []
  );

  // ========= Config =========
  const brand = useMemo(
    () => ({
      name: "EBS Software Solution",
      email: "info@ebssoftware.co.in",
      phoneTop: "+91 99320 13501",
      salesPhone: "+91 97337 2 1111",
      gst: "19AJAPM8399K1ZE",
      pageUrl: "https://example.com/teasale", // change to your live URL
    }),
    []
  );

  const product = useMemo(
    () => ({
      name: "TeaSale",
      tagline: "Tea Manufacturer Billing, Stock & Accounts Suite",
      description:
        "TeaSale is developed for Tea Manufacturers and Head Office operations—covering billing, blending, auction sales, withdrawals, inventory control, and accounting with powerful MIS reporting.",
      price: "Rs. 75,000/- (1 licence) Multi-User + GST",
    }),
    []
  );

  // ========= SEO (simple + JSON-LD) =========
  const schemaMarkup = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: product.name,
      applicationCategory: "BusinessApplication",
      operatingSystem: ["Windows"],
      description: product.description,
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        availability: "https://schema.org/InStock",
      },
    }),
    [product]
  );

  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${product.name} | Tea Sales & Stock Software | ${brand.name}`;

    const head = document.head;
    const cleanup = () => {
      head.querySelectorAll('[data-teasale="1"]').forEach((n) => n.remove());
    };
    cleanup();

    const metaDesc = document.createElement("meta");
    metaDesc.setAttribute("name", "description");
    metaDesc.setAttribute("content", product.description);
    metaDesc.setAttribute("data-teasale", "1");
    head.appendChild(metaDesc);

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schemaMarkup);
    script.setAttribute("data-teasale", "1");
    head.appendChild(script);

    return () => {
      cleanup();
      document.title = prevTitle;
    };
  }, [brand.name, product.description, product.name, schemaMarkup]);

  // ========= Helpers =========
  const scrollToId = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const IconChip = ({ tone = "primary" }) => {
    const bg =
      tone === "success"
        ? theme.success
        : `linear-gradient(135deg, ${theme.accent}, ${theme.accent2})`;
    return (
      <span
        aria-hidden="true"
        style={{
          width: 36,
          height: 36,
          borderRadius: 14,
          background: bg,
          boxShadow: theme.shadowSoft,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
    );
  };

  // ========= Data =========
  const heroTags = useMemo(
    () => ["Billing", "Blending", "Auction Sales", "Inventory", "Accounting", "MIS Reports"],
    []
  );

  const highlights = useMemo(
    () => [
      {
        title: "Tea Billing & Invoicing",
        desc: "GST billing for general sales, private auctions, export invoices and more.",
      },
      {
        title: "Blending & Lot Control",
        desc: "Blending workflows with grade/lot visibility and tracking registers.",
      },
      {
        title: "Stock & Valuation",
        desc: "Warehouse/grade/lot-wise stock with valuation and ledger reporting.",
      },
      {
        title: "Accounting & Compliance",
        desc: "Cash/Bank, ledger, TB, P&L, GST registers and reconciliation support.",
      },
    ],
    []
  );

  const featureGroups = useMemo(
    () => [
      {
        title: "Entry Module",
        bullets: [
          "Arrival Entry (Factory Invoice)",
          "Purchase from Outside of Garden",
          "Inter Godown Transfer",
          "Blending",
          "Delivery Order",
          "Sales Advice",
          "Tea Withdrawn",
          "GST General Sales Invoice",
          "Auction & Private Auctions Billing",
          "Export Sales",
          "Excess / Shortage",
          "Misc. Purchase Entry",
          "Purchase (Service)",
          "GST Reverse Entry",
          "Receipt / Payment / Contra / Journal",
          "Debit Note / Credit Note",
          "Voucher Manage Confirmation",
        ],
      },
      {
        title: "MIS Reports",
        bullets: [
          "Factory Invoice Register",
          "Transit Invoice Register",
          "Inward / Outward Stock Transfer Reports",
          "Purchase Register (Goods & Services)",
          "Delivery Register",
          "Sales Advice Register",
          "Invoice Register (General/Private GST Sale)",
          "Export Invoice Register",
          "Overall Sales Register",
          "Auction Sales Register (Generated IRN by Tea Board)",
          "Sales Return Register",
          "Tea Withdrawn Register",
          "Inter Godown Transfer Register",
          "Purchase Register (Tea)",
          "Tea Blending Register",
          "Blend Number Wise Register",
          "LOT Query",
          "Grade Wise Average Register",
        ],
      },
      {
        title: "Stock Reports",
        bullets: [
          "Stock Summary (Overall)",
          "Closing Stock (Overall) with Valuation",
          "Stock Summary (Grade / Warehouse / Lot / Category / Arrival Wise)",
          "Closing Stock (Grade / Warehouse / Lot / Category / Arrival Wise)",
          "Stock Ledger",
          "Closing Stock Export to Excel (for blending import)",
        ],
      },
      {
        title: "Outstanding Reports",
        bullets: [
          "Debtor's Balance Summary",
          "Aging Analysis (Debtors / Creditors)",
          "Creditor's Balance Summary",
          "Aging Analysis (Bill-by-bill Analysis)",
        ],
      },
      {
        title: "Accounting Reports",
        bullets: [
          "Cash Bank Book",
          "Journal Book",
          "Debit Note Register",
          "Credit Note Register",
          "Purchase Register / Purchase Return Register",
          "Sales Register / Sales Return Register",
          "Ledger",
          "Trial Balance",
          "Expense Trial",
          "Trading and Profit & Loss Statement",
          "Bank Reconciliation",
          "Interest Calculation",
          "Confirmation of Accounts",
          "Statement of Expenditure",
          "Cost Centre Reports",
          "GST Type Wise Sales Register",
          "GSTR (GSTR-1, GSTR-2, GSTR-3B) Reports",
        ],
      },
    ],
    []
  );

  // ========= Styles =========
  const styles = {
    page: {
      background: theme.bg,
      minHeight: "100vh",
      color: theme.slate,
      fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    },

    // top small info bar
    topBar: {
      background: theme.navy,
      color: "#e2e8f0",
      fontSize: 13,
      borderBottom: "1px solid rgba(255,255,255,0.08)",
    },
    topBarInner: {
      padding: "8px 0",
      display: "flex",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      alignItems: "center",
    },
    topLink: {
      color: "#e2e8f0",
      textDecoration: "none",
      fontWeight: 800,
    },

    // local product nav (optional; if you already have global navbar, you can remove this)
    navWrap: {
      position: "sticky",
      top: 0,
      zIndex: 30,
      background: "rgba(246,248,251,0.9)",
      backdropFilter: "blur(12px)",
      borderBottom: `1px solid ${theme.border}`,
    },
    navInner: {
      padding: "14px 0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 14,
      flexWrap: "wrap",
    },
    brandRow: { display: "flex", alignItems: "center", gap: 12 },
    logo: {
      width: 44,
      height: 44,
      borderRadius: 14,
      background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent2})`,
      boxShadow: theme.shadowSoft,
      flexShrink: 0,
    },
    brandName: { margin: 0, fontWeight: 950, color: theme.ink, letterSpacing: -0.2, fontSize: 16 },
    brandSub: { margin: 0, color: theme.muted, fontWeight: 750, fontSize: 12 },

    // hero
    hero: {
      marginTop: 18,
      borderRadius: 24,
      overflow: "hidden",
      position: "relative",
      background: theme.navy,
      boxShadow: theme.shadow,
      border: `1px solid ${theme.border}`,
    },
    heroImg: {
      width: "100%",
      height: 520,
      objectFit: "cover",
      opacity: 0.25,
      filter: "grayscale(100%) contrast(120%)",
      transform: "scale(1.02)",
    },
    heroOverlay: {
      position: "absolute",
      inset: 0,
      background:
        "radial-gradient(900px 600px at 18% 25%, rgba(29,78,216,0.35), transparent 60%), radial-gradient(900px 500px at 85% 15%, rgba(14,165,233,0.25), transparent 55%), linear-gradient(180deg, rgba(11,18,32,0.45), rgba(11,18,32,0.92))",
    },
    heroContent: {
      position: "absolute",
      inset: 0,
      padding: "clamp(18px, 4vw, 56px)",
      display: "flex",
      alignItems: "center",
    },
    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 999,
      background: "rgba(255,255,255,0.10)",
      border: "1px solid rgba(255,255,255,0.18)",
      color: "#dbeafe",
      fontWeight: 900,
      textTransform: "uppercase",
      letterSpacing: 1,
      fontSize: 12,
      width: "fit-content",
    },
    h1: {
      marginTop: 14,
      marginBottom: 10,
      fontSize: "clamp(2.2rem, 4.2vw, 3.4rem)",
      fontWeight: 950,
      color: "#fff",
      letterSpacing: -0.8,
      lineHeight: 1.05,
      textShadow: "0 10px 30px rgba(0,0,0,0.35)",
    },
    heroP: {
      color: "#e2e8f0",
      lineHeight: 1.9,
      fontSize: 16,
      maxWidth: 700,
      marginBottom: 18,
    },
    tagRow: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 },
    tag: {
      padding: "8px 12px",
      borderRadius: 999,
      color: "#e2e8f0",
      background: "rgba(255,255,255,0.10)",
      border: "1px solid rgba(255,255,255,0.18)",
      fontWeight: 850,
      fontSize: 13,
    },
    heroCard: {
      background: "rgba(255,255,255,0.97)",
      borderRadius: 18,
      border: "1px solid rgba(255,255,255,0.60)",
      boxShadow: "0 30px 70px rgba(2,6,23,0.25)",
    },

    // sections/cards
    section: { padding: "56px 0" },
    sectionTitle: { margin: 0, fontSize: 28, fontWeight: 950, color: theme.ink, letterSpacing: -0.4 },
    sectionSub: { marginTop: 10, marginBottom: 0, color: theme.muted, lineHeight: 1.85, maxWidth: 900 },

    featureCard: {
      height: "100%",
      borderRadius: 18,
      border: `1px solid ${theme.border}`,
      background: theme.surface,
      boxShadow: theme.shadowSoft,
      padding: 18,
    },
    featureTitle: { margin: 0, fontSize: 16, fontWeight: 950, color: theme.ink, letterSpacing: -0.2 },
    featureDesc: { margin: "8px 0 0 0", color: theme.muted, lineHeight: 1.8, fontSize: 14 },

    moduleCard: {
      height: "100%",
      borderRadius: 18,
      border: `1px solid ${theme.border}`,
      background: theme.surface,
      boxShadow: theme.shadowSoft,
      overflow: "hidden",
    },
    moduleHead: {
      padding: 18,
      borderBottom: `1px solid ${theme.border}`,
      background: "linear-gradient(180deg, rgba(29,78,216,0.08), rgba(255,255,255,0))",
    },
    moduleTitle: { margin: 0, fontWeight: 950, color: theme.ink, fontSize: 16 },
    moduleList: { listStyle: "none", margin: 0, padding: 18, display: "grid", gap: 10 },
    moduleItem: { display: "flex", gap: 10, alignItems: "flex-start", color: theme.slate, lineHeight: 1.75, fontSize: 14 },

    cta: {
      borderRadius: 26,
      background: theme.navy,
      color: "#fff",
      position: "relative",
      overflow: "hidden",
      boxShadow: theme.shadow,
      border: "1px solid rgba(255,255,255,0.12)",
      marginTop: 8,
    },
    ctaOverlay: {
      position: "absolute",
      inset: 0,
      background:
        "radial-gradient(900px 500px at 20% 30%, rgba(29,78,216,0.45), transparent 60%), radial-gradient(800px 450px at 80% 20%, rgba(14,165,233,0.30), transparent 55%), linear-gradient(180deg, rgba(11,18,32,0.35), rgba(11,18,32,0.92))",
    },
    ctaBody: {
      position: "relative",
      padding: "30px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
    },
    ctaTitle: {
      margin: 0,
      fontWeight: 950,
      letterSpacing: -0.4,
      color: "#ffffff",
      fontSize: "clamp(1.4rem, 2.2vw, 2rem)",
      textShadow: "0 10px 26px rgba(0,0,0,0.35)",
    },
    ctaText: { margin: "10px 0 0 0", color: "#dbeafe", lineHeight: 1.85, fontSize: 15, maxWidth: 760 },
    footer: { padding: "26px 0 40px", color: theme.muted, fontSize: 13 },
  };

  return (
    <div style={styles.page}>
      {/* Optional top bar (remove if you already have site-wide top bar) */}
      <div style={styles.topBar}>
        <Container>
          <div style={styles.topBarInner}>
            <div className="d-flex gap-3 flex-wrap">
              <span>
                Email:{" "}
                <a style={styles.topLink} href={`mailto:${brand.email}`}>
                  {brand.email}
                </a>
              </span>
              <span>
                Sales:{" "}
                <a style={styles.topLink} href={`tel:${brand.salesPhone.replace(/\s/g, "")}`}>
                  {brand.salesPhone}
                </a>
              </span>
            </div>
            <div style={{ fontWeight: 900 }}>GSTIN: {brand.gst}</div>
          </div>
        </Container>
      </div>

      {/* Local product nav (remove if your global Navbar is enough) */}
      <div style={styles.navWrap}>
        <Container>
          <div style={styles.navInner}>
            <div style={styles.brandRow}>
              <div style={styles.logo} aria-hidden="true" />
              <div>
                <p style={styles.brandName}>{product.name}</p>
                <p style={styles.brandSub}>{product.tagline}</p>
              </div>
            </div>

            <Nav className="d-none d-md-flex" style={{ gap: 14 }}>
              <Nav.Link onClick={() => scrollToId("overview")} style={{ color: theme.slate, fontWeight: 850 }}>
                Overview
              </Nav.Link>
              <Nav.Link onClick={() => scrollToId("features")} style={{ color: theme.slate, fontWeight: 850 }}>
                Features
              </Nav.Link>
              <Nav.Link onClick={() => scrollToId("modules")} style={{ color: theme.slate, fontWeight: 850 }}>
                Reports
              </Nav.Link>
              <Nav.Link onClick={() => scrollToId("contact")} style={{ color: theme.slate, fontWeight: 850 }}>
                Contact
              </Nav.Link>
            </Nav>

            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="outline-primary"
                style={{ borderRadius: 12, fontWeight: 900 }}
                onClick={() => navigate("/request-demo")}
              >
                Request Demo
              </Button>
              <Button
                variant="primary"
                style={{
                  borderRadius: 12,
                  fontWeight: 950,
                  background: theme.accent,
                  borderColor: theme.accent,
                }}
                onClick={() => navigate("/contact-sales")}
              >
                Contact Sales
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container>
        {/* ===== Hero ===== */}
        <div style={styles.hero}>
          <img src={web5} alt="TeaSale software banner" style={styles.heroImg} />
          <div style={styles.heroOverlay} />
          <div style={styles.heroContent}>
            <Row className="w-100 g-4 align-items-center">
              <Col lg={7}>
                <div style={styles.pill}>
                  <span
                    aria-hidden="true"
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: theme.accent2,
                    }}
                  />
                  Tea Manufacturer Suite
                </div>

                <h1 style={styles.h1}>{product.name}</h1>
                <p style={styles.heroP}>{product.description}</p>

                <div className="d-flex gap-2 flex-wrap">
                  <Badge bg="light" text="dark" style={{ borderRadius: 999, padding: "10px 12px", fontWeight: 900 }}>
                    {product.price}
                  </Badge>

                  <Button
                    variant="primary"
                    style={{ borderRadius: 12, fontWeight: 950, background: theme.accent2, borderColor: theme.accent2 }}
                    onClick={() => navigate("/request-demo")}
                  >
                    Get Demo
                  </Button>

                  <Button
                    variant="light"
                    style={{ borderRadius: 12, fontWeight: 950 }}
                    onClick={() => scrollToId("modules")}
                  >
                    View Reports
                  </Button>
                </div>

                <div style={styles.tagRow}>
                  {heroTags.map((t) => (
                    <span key={t} style={styles.tag}>
                      {t}
                    </span>
                  ))}
                </div>
              </Col>

              <Col lg={5}>
                <Card style={styles.heroCard}>
                  <Card.Body style={{ padding: 18 }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <div style={{ fontWeight: 950, color: theme.ink, fontSize: 15 }}>Pricing</div>
                        <div style={{ color: theme.muted, fontWeight: 750, fontSize: 12, marginTop: 6 }}>
                          Multi-user license
                        </div>
                      </div>
                      <Badge bg="primary" style={{ borderRadius: 999, fontWeight: 900 }}>
                        INR
                      </Badge>
                    </div>

                    <div
                      style={{
                        marginTop: 12,
                        border: `1px solid ${theme.border}`,
                        borderRadius: 14,
                        padding: 12,
                        background: theme.surface,
                      }}
                    >
                      <div style={{ color: theme.muted, fontWeight: 850, fontSize: 12 }}>Price (1 licence)</div>
                      <div style={{ color: theme.ink, fontWeight: 950, fontSize: 16, marginTop: 6 }}>{product.price}</div>
                    </div>

                    <div style={{ marginTop: 12, color: theme.muted, fontSize: 13, lineHeight: 1.7 }}>
                      <b style={{ color: theme.ink }}>Sales:</b>{" "}
                      <a href={`tel:${brand.salesPhone.replace(/\s/g, "")}`} style={{ textDecoration: "none" }}>
                        {brand.salesPhone}
                      </a>
                      <br />
                      Demo available. Contact us for installation & onboarding.
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </div>

        {/* ===== Overview ===== */}
        <section id="overview" style={styles.section}>
          <Row className="g-4 align-items-start">
            <Col lg={5}>
              <h2 style={styles.sectionTitle}>Overview</h2>
              <p style={styles.sectionSub}>
                TeaSale supports tea manufacturer billing, inventory control and accounting with robust MIS & compliance reports.
                Manage blending, auction/private sales, export invoices and withdrawals with structured tracking.
              </p>
            </Col>

            <Col lg={7}>
              <Row className="g-3">
                {highlights.map((h) => (
                  <Col md={6} key={h.title}>
                    <div style={styles.featureCard}>
                      <div className="d-flex align-items-center gap-2">
                        <IconChip />
                        <h3 style={styles.featureTitle}>{h.title}</h3>
                      </div>
                      <p style={styles.featureDesc}>{h.desc}</p>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </section>

        {/* ===== Features/Reports (Groups) ===== */}
        <section id="modules" style={{ ...styles.section, paddingTop: 0 }}>
          <Row className="mb-3">
            <Col lg={8}>
              <h2 style={styles.sectionTitle}>Modules & Reports</h2>
              <p style={styles.sectionSub}>
                Entry, MIS, Stock, Outstanding and Accounting reports designed for day-to-day operations and management review.
              </p>
            </Col>
          </Row>

          <Row className="g-3">
            {featureGroups.map((g) => (
              <Col lg={6} key={g.title}>
                <Card style={styles.moduleCard}>
                  <div style={styles.moduleHead}>
                    <h3 style={styles.moduleTitle}>{g.title}</h3>
                  </div>
                  <ul style={styles.moduleList}>
                    {g.bullets.map((b) => (
                      <li key={b} style={styles.moduleItem}>
                        <span
                          aria-hidden="true"
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            background: theme.success,
                            marginTop: 6,
                            flexShrink: 0,
                          }}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        {/* ===== CTA ===== */}
        <section id="contact" style={{ paddingBottom: 56 }}>
          <div style={styles.cta}>
            <div style={styles.ctaOverlay} />
            <div style={styles.ctaBody}>
              <div>
                <h2 style={styles.ctaTitle}>Need a demo or pricing confirmation?</h2>
                <p style={styles.ctaText}>
                  Call sales at{" "}
                  <a
                    href={`tel:${brand.salesPhone.replace(/\s/g, "")}`}
                    style={{ color: "#fff", fontWeight: 900, textDecoration: "underline" }}
                  >
                    {brand.salesPhone}
                  </a>{" "}
                  or submit a demo request—our team will guide you.
                </p>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="light"
                  style={{ borderRadius: 12, fontWeight: 950 }}
                  onClick={() => navigate("/request-demo")}
                >
                  Request Demo
                </Button>
                <Button
                  variant="primary"
                  style={{
                    borderRadius: 12,
                    fontWeight: 950,
                    background: theme.accent2,
                    borderColor: theme.accent2,
                  }}
                  onClick={() => navigate("/contact-sales")}
                >
                  Contact Sales
                </Button>
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            © {new Date().getFullYear()} {brand.name}. All rights reserved.
          </div>
        </section>
      </Container>
    </div>
  );
};

export default TeaSale;
